import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { addToCart } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import Layout from '../components/Layout';
import PageMeta from '../components/PageMeta';
import AvailabilityBadge from '../components/AvailabilityBadge';
import SearchBar from '../components/SearchBar';
import { fetchProduct, fetchProducts } from '../services/products';
import { getStorageUrl } from '../services/api';
import './ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [sizeQuantities, setSizeQuantities] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    specifications: false
  });

  const getImageUrl = (path) => getStorageUrl(path);

  const imageList = useMemo(() => {
    if (!product) return [];
    const main = product.image ? [product.image] : [];
    const extra = (product.images || []).map((img) => img.image_path || img).filter(Boolean);
    return [...main, ...extra].map(getImageUrl).filter(Boolean);
  }, [product]);

  const sizesFromApi = useMemo(() => {
    const specs = product?.specifications || product?.variants || [];
    return specs.map((s) => ({
      id: s.id,
      size: s.spec_name && s.spec_value ? `${s.spec_name}: ${s.spec_value}` : (s.spec_value || s.spec_name || ''),
      price: s.price,
      available: s.available !== false
    }));
  }, [product]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProduct(id);
        if (cancelled) return;
        setProduct(data);
        const initialQuantities = {};
        (data.specifications || data.variants || []).forEach((s) => {
          initialQuantities[s.id] = 0;
        });
        setSizeQuantities(initialQuantities);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load product.');
          setProduct(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!product || !product.id) return;
    let cancelled = false;
    const loadRelated = async () => {
      try {
        const res = await fetchProducts({ per_page: 8 });
        const list = res.data || [];
        const other = list.filter((p) => p.id !== product.id).slice(0, 4);
        if (!cancelled) setRelatedProducts(other);
      } catch (_) {
        if (!cancelled) setRelatedProducts([]);
      }
    };
    loadRelated();
    return () => { cancelled = true; };
  }, [product?.id]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderStars = (rating) => {
    const r = Math.min(5, Math.max(0, Number(rating) || 0));
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < r ? 'filled' : ''}`}>★</span>
    ));
  };

  const handleSizeQuantityChange = (sizeId, value) => {
    const numValue = parseInt(value, 10) || 0;
    if (numValue < 0) return;
    setSizeQuantities((prev) => ({ ...prev, [sizeId]: numValue }));
  };

  const getTotalSelectedQuantity = () => {
    return Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getSelectedSizes = () => {
    return sizesFromApi.filter((s) => (sizeQuantities[s.id] || 0) > 0);
  };

  const handleAddToCart = () => {
    if (sizesFromApi.length > 0) {
      const selectedSizes = getSelectedSizes();
      if (selectedSizes.length === 0) {
        alert('Please select at least one variant and quantity');
        return;
      }
      selectedSizes.forEach((size) => {
        const quantity = sizeQuantities[size.id];
        if (quantity > 0) {
          dispatch(addToCart({
            product: {
              ...product,
              selectedSize: size.size,
              sizeId: size.id,
              price: size.price
            },
            quantity
          }));
        }
      });
      alert('Products added to cart!');
      const resetQuantities = {};
      sizesFromApi.forEach((s) => { resetQuantities[s.id] = 0; });
      setSizeQuantities(resetQuantities);
    } else {
      dispatch(addToCart({ product, quantity: 1 }));
      alert('Product added to cart!');
    }
  };

  const handleBuyNow = () => {
    if (sizesFromApi.length > 0) {
      const selectedSizes = getSelectedSizes();
      if (selectedSizes.length === 0) {
        alert('Please select at least one variant and quantity');
        return;
      }
      const orderItems = selectedSizes.map((size) => ({
        ...product,
        selectedSize: size.size,
        sizeId: size.id,
        price: size.price,
        quantity: sizeQuantities[size.id]
      }));
      navigate('/order', { state: { buyNowItems: orderItems } });
    } else {
      navigate('/order', { state: { buyNowItem: product } });
    }
  };

  const handleWhatsAppClick = () => {
    const text = encodeURIComponent(`Hi, I'm interested in: ${product?.name || 'this product'}`);
    window.open(`https://wa.me/2348143646922?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="product-details-page">
          <div className="loading">Loading product...</div>
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div className="product-details-page">
          <div className="breadcrumbs">
            <div className="breadcrumbs-container">
              <Link to="/">Home</Link>
              <span> &gt; </span>
              <Link to="/products">Products</Link>
            </div>
          </div>
          <div className="product-details-error">
            {error || 'Product not found.'}
            <button type="button" className="contact-btn" onClick={() => navigate('/products')}>
              Back to Products
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const keyFeatures = Array.isArray(product.key_features) ? product.key_features : [];
  const features = Array.isArray(product.features) ? product.features : [];
  const productSpecList = Array.isArray(product.product_specification) ? product.product_specification : [];
  const rating = product.rating != null ? product.rating : 0;
  const reviewsCount = product.reviews_count != null ? product.reviews_count : 0;

  const plainDesc = product.description ? String(product.description).replace(/<[^>]*>/g, '').trim() : '';
  const metaDescription = product.subtitle
    ? product.subtitle
    : (plainDesc.length > 160 ? `${plainDesc.slice(0, 157)}...` : plainDesc) || `Buy ${product.name} from FM Ultratech Farms. Quality agricultural products and greenhouse supplies.`;

  return (
    <Layout>
      <PageMeta
        title={product.name}
        description={metaDescription}
      />
      <SearchBar />
      <div className="product-details-page">
        <div className="breadcrumbs">
          <div className="breadcrumbs-container">
            <Link to="/">Home</Link>
            <span> &gt; </span>
            <Link to="/products">Products</Link>
            <span> &gt; </span>
            <span>{product.name}</span>
          </div>
        </div>

        <div className="product-details-content">
          <div className="product-details-container">
            <div className="product-main-section">
              <div className="product-images">
                <div className="main-image">
                  {imageList[selectedImage] ? (
                    <img src={imageList[selectedImage]} alt={product.name} />
                  ) : (
                    <div className="main-image-placeholder">No image</div>
                  )}
                </div>
                {imageList.length > 1 && (
                  <div className="thumbnail-images">
                    {imageList.map((src, index) => (
                      <img
                        key={index}
                        src={src}
                        alt={`${product.name} ${index + 1}`}
                        className={selectedImage === index ? 'active' : ''}
                        onClick={() => setSelectedImage(index)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="product-info">
                <AvailabilityBadge availabilityType={product.availability_type} size="large" />
                <h1 className="product-title">{product.name}</h1>
                {product.subtitle && <p className="product-subtitle">{product.subtitle}</p>}

                <div className="product-rating">
                  <div className="stars">{renderStars(rating)}</div>
                  <span className="reviews-count">{reviewsCount} reviews</span>
                </div>

                {keyFeatures.length > 0 && (
                  <div className="key-features">
                    {keyFeatures.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span className="check-icon">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                {sizesFromApi.length > 0 && (
                  <div className="size-selection-section">
                    <h3 className="size-selection-title">Select variant & quantity</h3>
                    <div className="sizes-list">
                      {sizesFromApi.map((size) => (
                        <div key={size.id} className="size-item">
                          <div className="size-info">
                            <span className="size-label">{size.size}</span>
                            <span className="size-price">{formatNaira(toDisplayNaira(size.price, nairaPerYuan))}</span>
                            {!size.available && <span className="size-unavailable">Out of Stock</span>}
                          </div>
                          <div className="size-quantity-control">
                            <button
                              type="button"
                              className="quantity-btn-decrease"
                              onClick={() => handleSizeQuantityChange(size.id, (sizeQuantities[size.id] || 0) - 1)}
                              disabled={(sizeQuantities[size.id] || 0) <= 0 || !size.available}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={sizeQuantities[size.id] || 0}
                              onChange={(e) => handleSizeQuantityChange(size.id, e.target.value)}
                              disabled={!size.available}
                              className="size-quantity-input"
                            />
                            <button
                              type="button"
                              className="quantity-btn-increase"
                              onClick={() => handleSizeQuantityChange(size.id, (sizeQuantities[size.id] || 0) + 1)}
                              disabled={!size.available}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {getTotalSelectedQuantity() > 0 && (
                      <div className="selected-total">Total items: {getTotalSelectedQuantity()}</div>
                    )}
                  </div>
                )}

                <div className="product-actions">
                  <button type="button" className="buy-now-btn" onClick={handleBuyNow}>
                    Buy Now &gt;
                  </button>
                  <button type="button" className="add-to-cart-btn" onClick={handleAddToCart}>
                    Add to Cart
                  </button>
                  <button type="button" className="whatsapp-btn" onClick={handleWhatsAppClick}>
                    Whatsapp us
                  </button>
                </div>

                <div className="assistance-section">
                  <p>Need help with installation or advice?</p>
                  <Link to="/contact">
                    <button type="button" className="contact-btn">Contact Us</button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="product-details-grid">
              <div className="product-details-left">
                {product.description && (
                  <section className="info-section product-description-section">
                    <h2>Description</h2>
                    <p className="product-description-full">{product.description}</p>
                  </section>
                )}

                {(product.product_info || features.length > 0 || product.applications) && (
                  <section className="info-section">
                    <h2>Product information</h2>
                    {product.product_info && <p>{product.product_info}</p>}
                    {features.length > 0 && (
                      <div className="features-list">
                        {features.map((feat, index) => (
                          <div key={index} className="feature-item">
                            <span className="check-icon">✓</span>
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {product.applications && (
                      <p className="applications">
                        <strong>Applications:</strong> {product.applications}
                      </p>
                    )}
                  </section>
                )}

                {productSpecList.length > 0 && (
                  <div className="collapsible-section">
                    <button
                      type="button"
                      className="collapsible-header"
                      onClick={() => toggleSection('specifications')}
                    >
                      Specifications {expandedSections.specifications ? '▼' : '→'}
                    </button>
                    {expandedSections.specifications && (
                      <div className="collapsible-content">
                        <ul>
                          {productSpecList.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="product-details-right">
                {productSpecList.length > 0 && (
                  <section className="info-section">
                    <h2>Key specifications</h2>
                    <ul className="specifications-list">
                      {productSpecList.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}

                <section className="info-section">
                  <h2>Why choose FM Ultratech Farms?</h2>
                  <ul className="why-choose-list">
                    <li>High-quality products</li>
                    <li>Experienced support team</li>
                    <li>Competitive pricing</li>
                    <li>Fast &amp; reliable delivery</li>
                  </ul>
                </section>

                <section className="info-section">
                  <h2>Trusted by farmers across Nigeria</h2>
                  <div className="trust-rating">
                    <div className="stars">{renderStars(5)}</div>
                    <span className="reviews-count">250+ reviews</span>
                  </div>
                  <Link to="/contact">
                    <button type="button" className="contact-btn">Contact Us &gt;</button>
                  </Link>
                </section>
              </div>
            </div>

            {relatedProducts.length > 0 && (
              <section className="related-products-section">
                <h2>Related products</h2>
                <div className="related-products-grid">
                  {relatedProducts.map((related) => (
                    <div
                      key={related.id}
                      className="related-product-card"
                      onClick={() => navigate(`/products/${related.id}`)}
                    >
                      <div className="related-product-image">
                        {getImageUrl(related.image) ? (
                          <img src={getImageUrl(related.image)} alt={related.name} />
                        ) : (
                          <div className="related-image-placeholder">No image</div>
                        )}
                      </div>
                      <h3>{related.name}</h3>
                      <button type="button" className="view-details-btn">
                        View Details &gt;
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetails;
